import { WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR } from "@/constants";
import { WISHLIST_WIDGET_NAMES } from "@/constants/widget-names";
import { ModalManager } from "@/modal-manager";
import {
    globalProductIdManagerToken,
    globalProductVariantsIdManagerToken,
} from "@/tokens";
import { AddToWishlistProductCardWidgetDefinition } from "@apps/wishlist/definitions";
import { ProductIdFromStaticProvider } from "@lib/ProductIdManager/ProductIdFromStaticProvider";
import { ProductIdManager } from "@lib/ProductIdManager/ProductIdManager";
import { ProductVariantIdFromStaticProvider } from "@lib/ProductVariantIdManager/ProductVariantIdFromStaticProvider";
import { ProductVariantsIdManager } from "@lib/ProductVariantIdManager/ProductVariantIdManager";
import AnalyticsEventsCollectorInterface from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsCollectorInterface";
import { AnalyticsEventsFactory } from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsFactory";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import {
    parseBooleanDataAttr,
    parseIntDataAttr,
    parseIntFromGidDataAttr,
    parseOptionalBooleanDataAttr,
    parseStringDataAttr,
} from "@ui-utils/Injector/utils";
import { container } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

/**
 * Example of placeholder:
 * <div
 *  style="display: block;"
 *  class="gw-add-to-wishlist-product-card-placeholder"
 *  data-gw-variant-id="{{ card_product.variants[0].id }}"
 *  data-gw-product-id="{{ card_product.id }}"
 *  data-gw-wishlist-counter-position="left-top"
 * ></div>
 */

const PRODUCT_ID_ATTR = "gw-product-id";
const VARIANT_ID_ATTR = "gw-variant-id";
const EXACT_VARIANT_ID_ATTR = "gw-exact-variant-id";
const WISHLIST_COUNTER_POSITION_ATTR = "gw-wishlist-counter-position";

class AddToWishlistProductCardWidgetInjector {
    constructor(
        private readonly placeholderSelector: string,
        private readonly logger: LoggerInterface,
        private readonly modalManager: ModalManager,
        private readonly eventsCollector: AnalyticsEventsCollectorInterface,
        private readonly analyticsEventsFactory: AnalyticsEventsFactory,
        private readonly dynamicImportManager: DynamicImportManager,
        private readonly moi: MutationObserverInjector
    ) {}

    injectCss(placeholder: HTMLElement) {
        placeholder.style.position = "absolute";
        placeholder.style.top = "0";
        placeholder.style.left = "0";
        placeholder.style.zIndex = "99";
        placeholder.style.lineHeight = "1";

        if (placeholder.parentElement) {
            placeholder.parentElement.style.position = "relative";
        }
    }

    start() {
        this.logger.debug("AddToWishlistProductCardWidgetInjector.start", {
            placeholderSelector: this.placeholderSelector,
        });

        const targets$ = this.moi.register({
            targetSelector: this.placeholderSelector,
        });

        targets$.subscribe((placeholder) => {
            this.logger.debug(
                "AddToWishlistProductCardWidgetInjector found placeholder",
                { placeholder }
            );

            this.injectCss(placeholder);

            const productIdFromDataAttr =
                parseIntDataAttr(placeholder, PRODUCT_ID_ATTR) ||
                parseIntFromGidDataAttr(placeholder, PRODUCT_ID_ATTR);

            const productIdManager =
                productIdFromDataAttr === null
                    ? container.resolve(globalProductIdManagerToken)
                    : new ProductIdManager();

            if (productIdFromDataAttr !== null) {
                productIdManager.registerProvider(
                    new ProductIdFromStaticProvider(productIdFromDataAttr)
                );
            }

            const variantIdFromDataAttr =
                parseIntDataAttr(placeholder, VARIANT_ID_ATTR) ||
                parseIntFromGidDataAttr(placeholder, VARIANT_ID_ATTR);

            const productVariantsIdManager =
                variantIdFromDataAttr === null
                    ? container.resolve(globalProductVariantsIdManagerToken)
                    : new ProductVariantsIdManager();

            if (variantIdFromDataAttr !== null) {
                productVariantsIdManager.registerProvider(
                    new ProductVariantIdFromStaticProvider(
                        variantIdFromDataAttr
                    )
                );
            }

            const exactVariantIdFromDataAttr = parseBooleanDataAttr(
                placeholder,
                EXACT_VARIANT_ID_ATTR
            );

            const wishlistCounterPositionFromDataAttr = parseStringDataAttr(
                placeholder,
                WISHLIST_COUNTER_POSITION_ATTR
            );

            void (async () => {
                const AddToWishlistProductCardWidget =
                    await this.dynamicImportManager.importDefault(
                        AddToWishlistProductCardWidgetDefinition,
                        true
                    );

                const widget = new AddToWishlistProductCardWidget(
                    {
                        target: placeholder,
                        isWidgetShadowRootEnabled: parseOptionalBooleanDataAttr(
                            placeholder,
                            WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                        ),
                        wishlistCounterPosition:
                            wishlistCounterPositionFromDataAttr === "left-top"
                                ? "left-top"
                                : "right-top",
                        exactVariantId: exactVariantIdFromDataAttr,
                    },
                    this.logger,
                    this.modalManager,
                    productIdManager,
                    productVariantsIdManager
                );

                await widget.init();

                this.eventsCollector.pushEvent(
                    this.analyticsEventsFactory.createWidgetInjected(
                        WISHLIST_WIDGET_NAMES.ADD_TO_WISHLIST_PRODUCT_CARD_WIDGET
                    )
                );

                dispatchSdkEvent(
                    new WidgetInjectedSdkEvent(
                        WISHLIST_WIDGET_NAMES.ADD_TO_WISHLIST_PRODUCT_CARD_WIDGET
                    )
                );
            })();
        });
    }
}

export default AddToWishlistProductCardWidgetInjector;
